import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Input } from '../components/ui/input';
import { MapPin, Loader2 } from 'lucide-react';

const AddressAutocomplete = ({ value, onChange, onAddressSelect, placeholder, className, ...props }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapperRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (value !== undefined && value !== query) {
      setQuery(value);
    }
  }, [value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchSuggestions = useCallback(async (text) => {
    if (!text || text.length < 3) {
      setSuggestions([]);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(text)}&limit=5`);
      const data = await res.json();
      setSuggestions(data.features || []);
      setIsOpen(true);
    } catch {
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e) => {
    const text = e.target.value;
    setQuery(text);
    if (onChange) onChange(text);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(text), 300);
  };

  const handleSelect = (feature) => {
    const props = feature.properties;
    const label = props.label || '';
    const postalCode = props.postcode || '';
    const city = props.city || '';

    setQuery(label);
    setIsOpen(false);
    setSuggestions([]);
    if (onChange) onChange(label);
    if (onAddressSelect) {
      onAddressSelect({
        label,
        postalCode,
        city,
        street: props.name || '',
        context: props.context || ''
      });
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div className="relative">
        <Input
          value={query}
          onChange={handleChange}
          onFocus={() => suggestions.length > 0 && setIsOpen(true)}
          placeholder={placeholder || "Saisissez une adresse..."}
          className={`rounded-none pr-8 ${className || ''}`}
          data-testid="address-autocomplete-input"
          {...props}
        />
        <div className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
        </div>
      </div>

      {isOpen && suggestions.length > 0 && (
        <ul
          className="absolute z-50 w-full bg-white border border-border shadow-lg max-h-60 overflow-y-auto mt-1"
          data-testid="address-suggestions"
        >
          {suggestions.map((feature, i) => (
            <li
              key={i}
              onClick={() => handleSelect(feature)}
              className="px-3 py-2.5 text-sm cursor-pointer hover:bg-surface transition-colors border-b border-border last:border-0 flex items-start gap-2"
              data-testid={`address-suggestion-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
              <div>
                <span className="font-medium">{feature.properties.name}</span>
                <span className="text-muted-foreground ml-1">
                  {feature.properties.postcode} {feature.properties.city}
                </span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AddressAutocomplete;
